/**
 * Free Gifts — auto-adds a gift set alongside qualifying subscription products.
 *
 * Works with snippets/free-gifts-data.liquid, which renders the gift data as a
 * JSON script tag inside the product form. When the customer adds a product
 * with a qualifying selling plan (prepaid → 4 gifts, standard → 3 gifts), the
 * main product and its gifts are added in a single atomic /cart/add.js call.
 *
 * Dedup rule (cart-wide, first product wins): if the cart already holds any
 * line with `_gift_category: <category>`, no new gift set is added for that
 * category. Prepaid and standard sets can coexist.
 *
 * Gift line item properties:
 *   _free_gift: 'true'
 *   _gift_category: 'prepaid' | 'standard'
 *
 * Server-side pricing (100% off) is enforced by the free-gifts discount
 * function — this file never sets prices.
 */
(function () {
  'use strict';

  var CATEGORY_CAPS = { prepaid: 4, standard: 4 };

  function dispatch(name) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (response) {
      if (!response.ok) {
        return response.json().then(
          function (err) { throw err; },
          function () { throw new Error('Request failed: ' + url); }
        );
      }
      return response.json();
    });
  }

  function getGiftData(form) {
    var script = form.querySelector('[data-free-gifts-data]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[free-gifts] Invalid gift data JSON', error);
      return null;
    }
  }

  function buildMainItem(formData, planId) {
    var item = {
      id: parseInt(formData.get('id'), 10),
      quantity: parseInt(formData.get('quantity'), 10) || 1,
      selling_plan: parseInt(planId, 10),
      properties: {}
    };
    formData.forEach(function (value, key) {
      var match = key.match(/^properties\[(.+)\]$/);
      if (match) item.properties[match[1]] = value;
    });
    return item;
  }

  function buildGiftItems(giftList, category) {
    return giftList.map(function (gift) {
      return {
        id: gift.id,
        quantity: 1,
        properties: {
          _free_gift: 'true',
          _gift_category: category
        }
      };
    });
  }

  function addItems(items) {
    return fetchJSON('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: items })
    });
  }

  function refreshCart() {
    var cartElement = document.querySelector('[data-cart-drawer]');
    if (cartElement && typeof cartElement.getCart === 'function') {
      cartElement.getCart();
    } else {
      window.location = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
    }
  }

  function setLoading(button, isLoading) {
    if (!button) return;
    button.classList.toggle('is-loading', isLoading);
    if (isLoading) {
      button.setAttribute('disabled', true);
    } else {
      button.removeAttribute('disabled');
    }
  }

  function showError(form, error) {
    var message =
      (error && (error.description || error.message)) ||
      'Unable to add to cart. Please try again.';
    var container = form.querySelector('[data-cart-errors-container]');
    if (container) {
      container.textContent = message;
      container.classList.add('is-visible');
    } else {
      alert(message);
    }
  }

  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest ? event.target.closest('[data-add-to-cart]') : null;
      if (!button) return;
      if (button.hasAttribute('disabled') || button.classList.contains('is-loading')) return;

      var form = button.closest('form');
      if (!form) return;

      var data = getGiftData(form);
      if (!data || !data.gifts) return;

      // Mirror the theme's submit payload to read exactly what would be posted.
      var formData = new FormData(form);
      var planId = formData.get('selling_plan');
      if (!planId) return; // one-time purchase — leave the native flow alone

      var category = data.plans && data.plans[planId];
      if (!category || !CATEGORY_CAPS[category]) return;

      var giftList = (data.gifts[category] || [])
        .filter(function (gift) { return gift.available; })
        .slice(0, CATEGORY_CAPS[category]);
      if (!giftList.length) return; // no addable gifts — native flow

      // We take over: single atomic add of main product + gift set.
      event.preventDefault();
      event.stopPropagation();

      var mainItem = buildMainItem(formData, planId);
      if (!mainItem.id) {
        showError(form, null);
        return;
      }

      setLoading(button, true);
      dispatch('autocart:before');

      fetchJSON('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (cart) {
          var categoryExists = cart.items.some(function (item) {
            return item.properties && item.properties._gift_category === category;
          });
          var items = [mainItem];
          if (!categoryExists) {
            items = items.concat(buildGiftItems(giftList, category));
          }
          return addItems(items).catch(function (error) {
            if (items.length === 1) throw error;
            // A gift line failed (e.g. went out of stock after page load) and
            // the atomic add was rejected — retry with the main product alone.
            console.warn('[free-gifts] Gift add failed, retrying without gifts', error);
            return addItems([mainItem]);
          });
        })
        .then(function () {
          setLoading(button, false);
          dispatch('autocart:updated');
          refreshCart();
        })
        .catch(function (error) {
          setLoading(button, false);
          dispatch('autocart:error');
          showError(form, error);
        });
    },
    true // capture phase — must run before the theme's own ATC handler
  );
})();
